import * as i0 from '@angular/core';
import { EventEmitter, ElementRef, inject, TemplateRef, Component, ChangeDetectionStrategy, HostBinding, Input, Output, ContentChild, HostListener, ContentChildren, ViewChild } from '@angular/core';
import { Subject, merge } from 'rxjs';
import { startWith, switchMap, filter, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BaseControlComponent, ControlContainerComponent } from '@knorr-bremse-portals/ngx-components/internal/controls';
import { DOWN_ARROW, UP_ARROW, LEFT_ARROW, RIGHT_ARROW, ENTER, SPACE, hasModifierKey, TAB, ESCAPE, BACKSPACE } from '@knorr-bremse-portals/ngx-components';
import { iconArrowDown, iconSearch, IconComponent } from '@knorr-bremse-portals/ngx-components/components/icon';
import { LabelComponent } from '@knorr-bremse-portals/ngx-components/components/label';

/**
 * Option IDs need to be unique across components, so this counter exists outside of
 * the component definition.
 */
let _uniqueIdCounter = 0;
class SelectOptionComponent {
    constructor() {
        this._selected = false;
        this.id = `kb-option-${_uniqueIdCounter++}`;
        /** Event emitted when the option is selected or deselected. */
        this.selectedChange = new EventEmitter();
        this._disabled = false;
        this.hostElement = inject(ElementRef);
    }
    get role() { return 'option'; }
    /** Whether or not the option is currently selected. */
    get selected() { return this._selected; }
    set selected(value) { this._selected = coerceBooleanProperty(value); }
    /** Whether the option is disabled. */
    get disabled() { return this._disabled; }
    set disabled(value) { this._disabled = coerceBooleanProperty(value); }
    getOffsetHeight() {
        return this.hostElement.nativeElement.offsetHeight;
    }
    getLabelText() {
        return this.hostElement.nativeElement.innerText;
    }
    select() {
        if (!this.disabled) {
            this._selected = true;
            this.selectedChange.next(this);
        }
    }
    deselect() {
        this._selected = false;
        this.selectedChange.next(this);
    }
    /**
     * Gets the `aria-selected` value for the option. We explicitly omit the `aria-selected`
     * attribute from single-selection, unselected options. Including the `aria-selected="false"`
     * attributes adds a significant amount of noise to screen-reader users without providing useful
     * information.
     */
    get ariaSelected() {
        return this.selected;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: SelectOptionComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.7", type: SelectOptionComponent, isStandalone: true, selector: "kb-select-option", inputs: { selected: "selected", id: "id", value: "value", disabled: "disabled" }, outputs: { selectedChange: "selectedChange" }, host: { listeners: { "click": "select()" }, properties: { "attr.role": "this.role", "id": "this.id", "attr.aria-disabled": "this.disabled", "attr.aria-selected": "this.ariaSelected" } }, queries: [{ propertyName: "label", first: true, predicate: TemplateRef, descendants: true }], ngImport: i0, template: "<ng-content></ng-content> <!-- Without this the label is rendered twice during ssr -->\n<ng-container *ngTemplateOutlet=\"label\"></ng-container>\n", styles: [":host{display:block;background-color:var(--kb-color--background);color:var(--kb-color--on-background);padding:var(--kb-gap--inner-column--text) var(--kb-gap--inner-row);transition:background-color var(--kb-speed--fast),color var(--kb-speed--fast);cursor:pointer}:host[aria-selected=true]{background-color:var(--kb-color--primary-variant);color:var(--kb-color--on-primary-variant)}:host[aria-disabled=true]{cursor:not-allowed;background-color:var(--kb-color--secondary-variant);color:var(--kb-color--on-background)}:host:not([aria-selected=true]):not([aria-disabled=true]):hover{background-color:var(--kb-color--primary);color:var(--kb-color--on-primary)}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: SelectOptionComponent, decorators: [{
            type: Component,
            args: [{ selector: 'kb-select-option', changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, imports: [CommonModule], template: "<ng-content></ng-content> <!-- Without this the label is rendered twice during ssr -->\n<ng-container *ngTemplateOutlet=\"label\"></ng-container>\n", styles: [":host{display:block;background-color:var(--kb-color--background);color:var(--kb-color--on-background);padding:var(--kb-gap--inner-column--text) var(--kb-gap--inner-row);transition:background-color var(--kb-speed--fast),color var(--kb-speed--fast);cursor:pointer}:host[aria-selected=true]{background-color:var(--kb-color--primary-variant);color:var(--kb-color--on-primary-variant)}:host[aria-disabled=true]{cursor:not-allowed;background-color:var(--kb-color--secondary-variant);color:var(--kb-color--on-background)}:host:not([aria-selected=true]):not([aria-disabled=true]):hover{background-color:var(--kb-color--primary);color:var(--kb-color--on-primary)}\n"] }]
        }], propDecorators: { role: [{
                type: HostBinding,
                args: ['attr.role']
            }], selected: [{
                type: Input
            }], id: [{
                type: Input
            }, {
                type: HostBinding
            }], selectedChange: [{
                type: Output
            }], value: [{
                type: Input
            }], disabled: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['attr.aria-disabled']
            }], label: [{
                type: ContentChild,
                args: [TemplateRef]
            }], select: [{
                type: HostListener,
                args: ['click']
            }], ariaSelected: [{
                type: HostBinding,
                args: ['attr.aria-selected']
            }] } });

const SEARCH_RESET_DELAY_MILLISECONDS = 800;
class SelectComponent extends BaseControlComponent {
    constructor() {
        super(...arguments);
        this.standalone = false;
        this.showRequired = true;
        this.valueChange = new EventEmitter();
        this.valueComparator = (value1, value2) => value1 == value2;
        this._open = false;
        //@HostBinding('attr.aria-invalid') // TODO
        /*@HostBinding('attr.aria-describedby')
        get ariaDescribedby() {
      
        }*/
        this.comboboxId = `kb-select-combobox-${this.uniqueId}`;
        this.listboxId = `kb-select-listbox-${this.uniqueId}`;
        this.iconArrowDown = iconArrowDown;
        this.iconSearch = iconSearch;
        /** `View -> model callback called when value changes` */
        this.onChange = () => undefined;
        /** `View -> model callback called when select has been touched` */
        this.onTouched = () => undefined;
        this.searchString = '';
        this.focusChanged = new Subject();
        this.skipChanges = false;
    }
    get value() { return this._value; }
    set value(newValue) {
        if (this.options) { // Reactive form triggers writeValue before public ngAfterContentInit, therefore _options is not populated
            this.options.forEach((option) => option.deselect());
            this.options.find((option) => option.value == newValue)?.select(); // == is required, as newValue can be null & option.value can be undefined
            // _value is set through select change handler
            this.ref.markForCheck(); // Needed for programmatic value updates e.g. ngModel
        }
        else {
            this._value = newValue;
        }
    }
    get open() { return this._open; }
    set open(value) {
        if (!this.disabled) {
            this._open = value;
            this.ref.markForCheck();
            if (this._open) {
                this.scrollToOption(this.selectedOption);
            }
        }
    }
    get ariaActiveDescendant() {
        return this._open ? this.selectedOption?.id : null;
    }
    ngAfterContentInit() {
        this.setupSelectChangeHandler();
        this.selectInitialOption();
        this.setupBlurHandler();
    }
    onOptionsPanelClick() {
        this.open = false;
    }
    selectInitialOption() {
        let selectedOption;
        const value = this._value;
        if (value !== null) {
            selectedOption = this.options.find((option) => option.value !== undefined && this.valueComparator(option.value, value));
        }
        else {
            if (!selectedOption) { // Get the first selected option
                selectedOption = this.options.find((option) => option.selected);
            }
            if (!selectedOption) { // Get the first option without a value
                selectedOption = this.options.find((option) => option.value === undefined);
            }
        }
        selectedOption?.select();
    }
    setupSelectChangeHandler() {
        this.options.changes.pipe(startWith(this.options), switchMap(() => merge(...this.options.map(option => option.selectedChange))), filter((option) => option.selected === true), takeUntilDestroyed(this.destroyRef)).subscribe((selectedOption) => {
            // Deselect non selected options
            this.options
                .filter((option) => option !== selectedOption)
                .forEach((option) => option.deselect());
            this.selectedOption = selectedOption;
            const newValue = selectedOption.value ?? null;
            if (!this.valueComparator(this._value, newValue)) {
                this._value = newValue;
                this.valueChange.emit(this.value);
                if (this.skipChanges === false) {
                    this.onChange(this.value);
                }
            }
            this.ref.markForCheck(); // This is needed if the currently selected option was removed and a new one was selected by _selectInitialOption
        });
        this.options.changes.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.selectInitialOption();
        });
    }
    get selectedOptionIndex() {
        return this.options.toArray().indexOf(this.selectedOption);
    }
    getSelectableOptions() {
        return this.options.filter(option => !option.disabled);
    }
    navigateOption(next) {
        const selectableOptions = this.getSelectableOptions();
        const targetOption = selectableOptions[((this.selectedOptionIndex + (next ? 1 : -1)) + selectableOptions.length) % selectableOptions.length];
        this.selectOption(targetOption);
    }
    selectNextOption() {
        this.navigateOption(true);
    }
    selectPreviousOption() {
        this.navigateOption(false);
    }
    selectOption(option) {
        if (option === undefined) {
            return;
        }
        this.value = option.value ?? null;
        this.scrollToOption(option);
    }
    scrollToOption(option) {
        if (!this._open) {
            return;
        }
        let height = 0;
        for (const currentOption of this.options) {
            if (currentOption === option) {
                break;
            }
            height += currentOption.getOffsetHeight();
        }
        this.optionsPanel.nativeElement.scrollTop = height;
    }
    /** Handles all keydown events on the select. */
    handleKeydown(event) {
        if (!this.disabled) {
            this.open ? this.handleOpenKeydown(event) : this.handleClosedKeydown(event);
        }
    }
    handleArrowSelect(key) {
        if (key === DOWN_ARROW) {
            this.selectNextOption();
        }
        else {
            this.selectPreviousOption();
        }
    }
    /** Handles keyboard events while the select is closed. */
    handleClosedKeydown(event) {
        const key = event.key;
        const isArrowKey = key === DOWN_ARROW || key === UP_ARROW ||
            key === LEFT_ARROW || key === RIGHT_ARROW;
        const isOpenKey = key === ENTER || key === SPACE;
        // Open the select on ALT + arrow key to match the native <select>
        if ((isOpenKey && !hasModifierKey(event)) || (event.altKey && isArrowKey)) {
            event.preventDefault(); // prevents the page from scrolling
            this.open = true;
            return;
        }
        const isVerticalArrowKey = key === DOWN_ARROW || key === UP_ARROW;
        // Open the select and select the next / previous element
        if (isVerticalArrowKey) {
            event.preventDefault(); // prevents the page from scrolling
            this.open = true;
            this.handleArrowSelect(key);
            return;
        }
    }
    /** Handles keyboard events when the selected is open. */
    handleOpenKeydown(event) {
        const key = event.key;
        const isVerticalArrowKey = key === DOWN_ARROW || key === UP_ARROW;
        if (isVerticalArrowKey && event.altKey || key === TAB || key === ESCAPE) {
            // Close the select on ALT + arrow key or Tab to match the native <select>
            event.preventDefault();
            this.open = false;
        }
        else if (isVerticalArrowKey) {
            this.handleArrowSelect(key);
            event.preventDefault(); // Prevent page scrolling
        }
        else if (key === ENTER && !hasModifierKey(event)) {
            this.open = false; // select already active
        }
        else if (key === BACKSPACE || key.length === 1) {
            this.handleKeyboardSearch(key);
        }
    }
    searchOptions(query) {
        const selectableOptions = this.getSelectableOptions();
        const searchStringArray = query.split('');
        const allSameCharacter = searchStringArray.every((character) => character === searchStringArray[0]);
        if (allSameCharacter) {
            const foundOptions = selectableOptions.filter(option => {
                return option.getLabelText().toLowerCase().startsWith(query[0]);
            });
            if (foundOptions.length === 0) {
                return undefined;
            }
            const selectedOptionIndex = foundOptions.indexOf(this.selectedOption);
            return foundOptions[(selectedOptionIndex + 1) % foundOptions.length];
        }
        return selectableOptions.find(option => {
            return option.getLabelText().toLowerCase().startsWith(query);
        });
    }
    handleKeyboardSearch(key) {
        if (this.searchStringTimeout !== undefined) {
            window.clearTimeout(this.searchStringTimeout);
        }
        this.searchStringTimeout = window.setTimeout(() => {
            this.searchString = '';
        }, SEARCH_RESET_DELAY_MILLISECONDS);
        if (key === BACKSPACE) {
            this.searchString = this.searchString.slice(0, -1);
        }
        else {
            this.searchString += key.toLowerCase();
        }
        const foundOption = this.searchOptions(this.searchString);
        if (foundOption !== undefined) {
            this.selectOption(foundOption);
        }
        else {
            this.searchString = '';
        }
    }
    onFocus() {
        this.focusChanged.next(true);
    }
    onBlur(event) {
        if (event.relatedTarget === this.optionsPanel.nativeElement) {
            this.comboboxElement.nativeElement.focus();
            return;
        }
        this.focusChanged.next(false);
    }
    setupBlurHandler() {
        this.focusChanged.pipe(debounceTime(1), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe((focused) => {
            if (!this.disabled && !focused) {
                this.open = false;
                this.onTouched();
            }
        });
    }
    writeValue(value) {
        this.skipChanges = true;
        this.value = value;
        this.skipChanges = false;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: SelectComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.7", type: SelectComponent, isStandalone: true, selector: "kb-select", inputs: { floating: "floating", on: "on", standalone: "standalone", showRequired: "showRequired", ariaLabel: ["aria-label", "ariaLabel"], ariaLabelledby: ["aria-labelledby", "ariaLabelledby"], value: "value", valueComparator: "valueComparator" }, outputs: { valueChange: "valueChange" }, host: { listeners: { "keydown": "handleKeydown($event)" } }, queries: [{ propertyName: "options", predicate: SelectOptionComponent, descendants: true }], viewQueries: [{ propertyName: "optionsPanel", first: true, predicate: ["optionsPanel"], descendants: true, read: ElementRef }, { propertyName: "comboboxElement", first: true, predicate: ["comboboxElement"], descendants: true, read: ElementRef }], usesInheritance: true, ngImport: i0, template: "<kb-control-container\n  *ngIf=\"!standalone; else selectTemplate\"\n  [showError]=\"showError\"\n  [controlId]=\"comboboxId\"\n  #controlContainerElement\n>\n  <ng-container *ngTemplateOutlet=\"selectTemplate; context: {controlContainerElement:controlContainerElement}\"></ng-container>\n  <kb-label><ng-content select=\"kb-label\"></ng-content> {{required && showRequired ? ' *' : ''}}</kb-label>\n  <ng-content select=\"kb-hint\" ngProjectAs=\"kb-hint\"></ng-content>\n  <ng-content select=\"kb-error\" ngProjectAs=\"kb-error\"></ng-content>\n</kb-control-container>\n\n<ng-template #selectTemplate let-controlContainerElement=\"controlContainerElement\">\n  <div\n    class=\"container\"\n    [class.open]=\"open\"\n  >\n    <div\n      [id]=\"comboboxId\"\n      class=\"selected\"\n      (click)=\"open = !open\"\n      (focus)=\"onFocus()\"\n      (blur)=\"onBlur($event)\"\n      role=\"combobox\"\n      [attr.aria-label]=\"ariaLabel\"\n      [attr.aria-labelledby]=\"controlContainerElement ? controlContainerElement.labelId : ariaLabelledby\"\n      [attr.aria-describedby]=\"controlContainerElement ? controlContainerElement.hintElement?.id : null\"\n      [attr.aria-controls]=\"listboxId\"\n      [attr.aria-expanded]=\"open\"\n      [attr.aria-activedescendant]=\"ariaActiveDescendant\"\n      aria-haspopup=\"listbox\"\n      [tabindex]=\"this.disabled ? '-1' : '0'\"\n      #comboboxElement\n    >\n      <ng-container *ngIf=\"selectedOption\"><ng-container *ngTemplateOutlet=\"selectedOption.label\"></ng-container></ng-container><kb-icon class=\"open-close-icon\" [icon]=\"iconArrowDown\"></kb-icon>\n    </div>\n    <div class=\"panel\">\n      <div\n        class=\"options kb-scrollbar\"\n        role=\"listbox\"\n        [id]=\"listboxId\"\n        [attr.aria-label]=\"ariaLabel\"\n        [attr.aria-labelledby]=\"controlContainerElement ? controlContainerElement.labelId : ariaLabelledby\"\n        tabindex=\"-1\"\n        (click)=\"onOptionsPanelClick()\"\n        #optionsPanel\n      >\n        <ng-content></ng-content>\n      </div>\n    </div>\n  </div>\n</ng-template>\n", styles: [":host{display:inline-block;font-size:var(--kb-typography--size-normal);line-height:var(--kb-typography--height-normal);color:var(--kb-color--on-background)}:host kb-control-container{width:100%;height:100%}:host[on=background] .container{color:var(--kb-color--on-background--high);border:.0625rem solid var(--kb-color--on-background--low)}:host[floating] .container{border:.0625rem solid transparent}:host.ng-invalid.ng-touched,:host.ng-invalid.kb-submitted{color:var(--kb-color--on-background--error)}:host[disabled] .container{cursor:not-allowed;background-color:var(--kb-color--secondary--surface-disabled);color:var(--kb-color--on-secondary--surface-disabled)}:host:not([disabled]) .selected{outline-offset:calc(var(--kb-width--outline) / 2 * -1);outline-width:var(--kb-width--outline)}:host:not([disabled]) .selected:focus-visible{outline-style:solid;outline-color:var(--kb-color--on-secondary--low)}:host .container{background-color:var(--kb-color--background);cursor:pointer;position:relative}:host .container .selected{display:flex;gap:var(--kb-gap--inner-row--text);padding:var(--kb-gap--inner-column--text) var(--kb-gap--inner-row);min-height:calc(1em + 2px);align-items:center}:host .container .selected .open-close-icon{transition:transform var(--kb-speed--fast);margin-left:auto}:host .container .panel{position:absolute;transition:transform var(--kb-speed--fast);transform-origin:top center;transform:rotateX(-90deg);background-color:inherit;z-index:10;max-height:13.75rem;display:flex;flex-direction:column;box-shadow:0 10px 16px #0000004d}:host .container .panel .options{overflow-y:auto}:host .container.open .panel{transform:rotateX(0)}:host .container.open .selected .open-close-icon{transform:rotate(180deg)}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: ControlContainerComponent, selector: "kb-control-container", inputs: ["controlId", "showError"] }, { kind: "component", type: LabelComponent, selector: "kb-label" }, { kind: "component", type: IconComponent, selector: "kb-icon, button[kb-icon], a[kb-icon]", inputs: ["icon"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: SelectComponent, decorators: [{
            type: Component,
            args: [{ selector: 'kb-select', changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, imports: [CommonModule, ControlContainerComponent, LabelComponent, IconComponent], template: "<kb-control-container\n  *ngIf=\"!standalone; else selectTemplate\"\n  [showError]=\"showError\"\n  [controlId]=\"comboboxId\"\n  #controlContainerElement\n>\n  <ng-container *ngTemplateOutlet=\"selectTemplate; context: {controlContainerElement:controlContainerElement}\"></ng-container>\n  <kb-label><ng-content select=\"kb-label\"></ng-content> {{required && showRequired ? ' *' : ''}}</kb-label>\n  <ng-content select=\"kb-hint\" ngProjectAs=\"kb-hint\"></ng-content>\n  <ng-content select=\"kb-error\" ngProjectAs=\"kb-error\"></ng-content>\n</kb-control-container>\n\n<ng-template #selectTemplate let-controlContainerElement=\"controlContainerElement\">\n  <div\n    class=\"container\"\n    [class.open]=\"open\"\n  >\n    <div\n      [id]=\"comboboxId\"\n      class=\"selected\"\n      (click)=\"open = !open\"\n      (focus)=\"onFocus()\"\n      (blur)=\"onBlur($event)\"\n      role=\"combobox\"\n      [attr.aria-label]=\"ariaLabel\"\n      [attr.aria-labelledby]=\"controlContainerElement ? controlContainerElement.labelId : ariaLabelledby\"\n      [attr.aria-describedby]=\"controlContainerElement ? controlContainerElement.hintElement?.id : null\"\n      [attr.aria-controls]=\"listboxId\"\n      [attr.aria-expanded]=\"open\"\n      [attr.aria-activedescendant]=\"ariaActiveDescendant\"\n      aria-haspopup=\"listbox\"\n      [tabindex]=\"this.disabled ? '-1' : '0'\"\n      #comboboxElement\n    >\n      <ng-container *ngIf=\"selectedOption\"><ng-container *ngTemplateOutlet=\"selectedOption.label\"></ng-container></ng-container><kb-icon class=\"open-close-icon\" [icon]=\"iconArrowDown\"></kb-icon>\n    </div>\n    <div class=\"panel\">\n      <div\n        class=\"options kb-scrollbar\"\n        role=\"listbox\"\n        [id]=\"listboxId\"\n        [attr.aria-label]=\"ariaLabel\"\n        [attr.aria-labelledby]=\"controlContainerElement ? controlContainerElement.labelId : ariaLabelledby\"\n        tabindex=\"-1\"\n        (click)=\"onOptionsPanelClick()\"\n        #optionsPanel\n      >\n        <ng-content></ng-content>\n      </div>\n    </div>\n  </div>\n</ng-template>\n", styles: [":host{display:inline-block;font-size:var(--kb-typography--size-normal);line-height:var(--kb-typography--height-normal);color:var(--kb-color--on-background)}:host kb-control-container{width:100%;height:100%}:host[on=background] .container{color:var(--kb-color--on-background--high);border:.0625rem solid var(--kb-color--on-background--low)}:host[floating] .container{border:.0625rem solid transparent}:host.ng-invalid.ng-touched,:host.ng-invalid.kb-submitted{color:var(--kb-color--on-background--error)}:host[disabled] .container{cursor:not-allowed;background-color:var(--kb-color--secondary--surface-disabled);color:var(--kb-color--on-secondary--surface-disabled)}:host:not([disabled]) .selected{outline-offset:calc(var(--kb-width--outline) / 2 * -1);outline-width:var(--kb-width--outline)}:host:not([disabled]) .selected:focus-visible{outline-style:solid;outline-color:var(--kb-color--on-secondary--low)}:host .container{background-color:var(--kb-color--background);cursor:pointer;position:relative}:host .container .selected{display:flex;gap:var(--kb-gap--inner-row--text);padding:var(--kb-gap--inner-column--text) var(--kb-gap--inner-row);min-height:calc(1em + 2px);align-items:center}:host .container .selected .open-close-icon{transition:transform var(--kb-speed--fast);margin-left:auto}:host .container .panel{position:absolute;transition:transform var(--kb-speed--fast);transform-origin:top center;transform:rotateX(-90deg);background-color:inherit;z-index:10;max-height:13.75rem;display:flex;flex-direction:column;box-shadow:0 10px 16px #0000004d}:host .container .panel .options{overflow-y:auto}:host .container.open .panel{transform:rotateX(0)}:host .container.open .selected .open-close-icon{transform:rotate(180deg)}\n"] }]
        }], propDecorators: { floating: [{
                type: Input
            }], on: [{
                type: Input
            }], standalone: [{
                type: Input
            }], showRequired: [{
                type: Input
            }], ariaLabel: [{
                type: Input,
                args: ['aria-label']
            }], ariaLabelledby: [{
                type: Input,
                args: ['aria-labelledby']
            }], value: [{
                type: Input
            }], valueChange: [{
                type: Output
            }], valueComparator: [{
                type: Input
            }], options: [{
                type: ContentChildren,
                args: [SelectOptionComponent, { descendants: true }]
            }], optionsPanel: [{
                type: ViewChild,
                args: ['optionsPanel', { read: ElementRef }]
            }], comboboxElement: [{
                type: ViewChild,
                args: ['comboboxElement', { read: ElementRef }]
            }], handleKeydown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

/**
 * Generated bundle index. Do not edit.
 */

export { SelectComponent, SelectOptionComponent };
